// src/features/patient/patientSlice.ts
//       // ❷ 환자 정보 수정 API 호출
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit"
import { PatientSerializer, PatientDetailSerializer } from "@/types/patient"
import { InsuranceSerializer, InsuranceEtcBaseSerializer } from "@/types/insurance"

import { fetchPatients, fetchPatient } from "@core/api/patientApi"

interface PatientPayload {
  patientlist: PatientSerializer[] | []
  selectedPatient: PatientDetailSerializer | null
  selectedInsurance: InsuranceSerializer | null
  // selectedInsurance: InsuranceExtendSerializer | null
  // selectedInsureancGs: InsuranceGSerializer[] | []
  loading: boolean
  error: string | null
  currentFetchPatientRequestId: string | null
  currentFetchPatientUuid: string | null
  lastAppliedFetchPatientRequestId: string | null
  fetchPatientRequestGeneration: number
  encounterRouteNavigationGeneration: number
}

const initialState: PatientPayload = {
  patientlist: [],
  selectedPatient: null,
  selectedInsurance: null,
  // selectedInsurance: null,
  // selectedInsureancGs: [],
  loading: false,
  error: null,
  currentFetchPatientRequestId: null,
  currentFetchPatientUuid: null,
  lastAppliedFetchPatientRequestId: null,
  fetchPatientRequestGeneration: 0,
  encounterRouteNavigationGeneration: 0,
}

const patientSlice = createSlice({
  name: "patient",
  initialState,
  reducers: {
    // setPatients(state, action: PayloadAction<PatientSerializer[]>) {
    //   state.patientlist = action.payload
    // },
    setSelectedInsurance(state, action: PayloadAction<InsuranceSerializer>) {
      state.selectedInsurance = action.payload
    },
    clearSelectedPatient(state) {
      state.selectedPatient = null
      state.selectedInsurance = null
    },
    clearSelectedInsurance(state) {
      state.selectedInsurance = null
    },
    applyEncounterRoutePatient(state, action: PayloadAction<PatientDetailSerializer>) {
      const selectedInsurance =
        action.payload.insurances?.find(insurance => insurance.insuranceUuid === action.payload.insuranceUuid) ||
        action.payload.insurances?.[0] ||
        null
      state.selectedPatient = {
        ...action.payload,
        insuranceUuid: selectedInsurance?.insuranceUuid || action.payload.insuranceUuid,
      }
      state.selectedInsurance = selectedInsurance
      state.error = null
    },
    beginEncounterRouteNavigation(state, action: PayloadAction<string>) {
      if (state.currentFetchPatientRequestId) {
        state.currentFetchPatientRequestId = null
        state.currentFetchPatientUuid = null
        state.loading = false
      }
      state.error = null
      state.encounterRouteNavigationGeneration += 1
    },
    invalidatePatientFetchForReception(state, action: PayloadAction<string | undefined>) {
      if (!action.payload || state.selectedPatient?.patientUuid !== action.payload || state.currentFetchPatientUuid !== action.payload) return
      state.currentFetchPatientRequestId = null
      state.currentFetchPatientUuid = null
      state.loading = false
    },
    // setInsurance(state, action: PayloadAction<Partial<InsuranceExtendSerializer>>) {
    //   state.selectedInsurance = {
    //     ...state.selectedInsurance,
    //     ...action.payload,
    //   }
    // },
    // setselectedInsureancGs(
    //   state,
    //   action: PayloadAction<InsuranceGSerializer[]>
    // ) {
    //   state.selectedInsureancGs = action.payload
    // },
    // clearInsuranceG(state) {
    //   state.selectedInsurance = null
    // },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    updateInsuranceUuid(state, action: PayloadAction<string>) {
      if (!state.selectedPatient) {
        state.selectedInsurance = null
        return
      }

      state.selectedPatient.insuranceUuid = action.payload

      if (!action.payload) {
        state.selectedInsurance = null
        return
      }

      state.selectedInsurance = state.selectedPatient.insurances?.find(ins => ins.insuranceUuid === action.payload) ?? null
    },
    patchSelectedPatient(state, action: PayloadAction<Partial<PatientDetailSerializer>>) {
      if (!state.selectedPatient) {
        return
      }

      state.selectedPatient = {
        ...state.selectedPatient,
        ...action.payload,
      }
    },
  },
  extraReducers: builder => {
    builder
      // fetchPatients 관련 처리
      .addCase(fetchPatients.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false
        state.patientlist = action.payload
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || action.error.message || "알 수 없는 오류 발생"
      })
      // fetchPatient 관련 처리
      .addCase(fetchPatient.pending, (state, action) => {
        state.loading = true
        state.error = null
        state.fetchPatientRequestGeneration = (state.fetchPatientRequestGeneration ?? 0) + 1
        state.currentFetchPatientRequestId = action.meta.requestId
        state.currentFetchPatientUuid = typeof action.meta.arg === "string" ? action.meta.arg : action.meta.arg.patientUuid
      })
      .addCase(fetchPatient.fulfilled, (state, action) => {
        if (state.currentFetchPatientRequestId !== action.meta.requestId) {
          return
        }

        state.loading = false
        state.currentFetchPatientRequestId = null
        state.currentFetchPatientUuid = null
        state.lastAppliedFetchPatientRequestId = action.meta.requestId
        // console.log("fetchPatient", action.payload)
        state.selectedPatient = action.payload
        state.selectedInsurance = null

        // insuranceUuid가 명시적으로 설정되어 있으면 해당 보험 찾기
        if (state.selectedPatient.insuranceUuid) {
          const matchedInsurance = state.selectedPatient.insurances?.find(ins => ins.insuranceUuid === state.selectedPatient?.insuranceUuid)
          if (matchedInsurance) {
            state.selectedInsurance = matchedInsurance
          } else if (state.selectedPatient.insurances && state.selectedPatient.insurances.length > 0) {
            // 매칭 실패 시 첫 번째 보험
            state.selectedInsurance = state.selectedPatient.insurances[0]
          }
        } else if (state.selectedPatient.insurances && state.selectedPatient.insurances.length > 0) {
          // insuranceUuid가 없으면 첫 번째 보험
          state.selectedInsurance = state.selectedPatient.insurances[0]
        }
      })
      .addCase(fetchPatient.rejected, (state, action) => {
        if (state.currentFetchPatientRequestId !== action.meta.requestId) {
          return
        }

        state.loading = false
        state.currentFetchPatientRequestId = null
        state.currentFetchPatientUuid = null
        state.error = (action.payload as string) || action.error.message || "알 수 없는 오류 발생"
      })
    // // 환자 검색
    // .addCase(searchPatients.pending, (state) => {
    //   state.loading = true
    //   state.error = null
    // })
    // .addCase(searchPatients.fulfilled, (state, action) => {
    //   state.loading = false
    //   state.patientlist = action.payload
    // })
    // .addCase(searchPatients.rejected, (state, action) => {
    //   state.loading = false
    //   state.error = action.payload ?? action.error.message ?? "검색 중 오류"
    // })
  },
})

export const {
  setSelectedInsurance,
  clearSelectedInsurance,
  clearSelectedPatient,
  applyEncounterRoutePatient,
  beginEncounterRouteNavigation,
  invalidatePatientFetchForReception,
  setLoading,
  setError,
  updateInsuranceUuid,
  patchSelectedPatient,
} = patientSlice.actions

export default patientSlice.reducer
